import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { findMinSelectableDate } from "../../../api_calls/campaigns";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import { RETAILER_SELECTION_OPTIONS_KEY } from "../../../api_calls/retailerSelectionConfig";
import { checkUserRole } from "../../../api_calls/userManagement";
import { deleteItems, updateStateData } from "../../../Store/actions";
import GroupedSingleSelect from "../Selectors/GroupedSingleSelect";
import SingleSelect from "../Selectors/SingleSelect";
import { buildGroupedSelectorOptions, sortSelectorOptions } from "../Selectors/selectorOptionUtils";
import {
  ROOT_RETAILER_GROUP_PARAM,
  STORE_FILTER_MODES,
  WORKSPACE_OPTIONS_KEY,
  loadRequestedStores,
  resolveBehaviorMode,
  resolveLoggedInUserType,
  resolveStoreIds,
  resolveStoreNames,
  resolveStoreRecordsForOptions,
  shouldUseRetailerStoreHierarchy,
  toStoreOptions,
  toStoreOptionsWithRootRetailer,
} from "./storeFilterUtils";

const STORE_PARENT_SELECTABLE = false;

const resolveDefaultStoreOption = ({
  groupedStoreOptions,
  parentSelectable,
  shouldUseRetailerHierarchy,
  sortedStoreOptions,
}) => {
  if (!shouldUseRetailerHierarchy) {
    return sortedStoreOptions[0];
  }

  const firstGroup = groupedStoreOptions[0];
  if (typeof firstGroup === "undefined") return undefined;

  // In grouped mode, "first option" means the first selectable row that users
  // see: the parent only when parent selection is explicitly enabled, otherwise
  // the first child under the first sorted parent.
  return parentSelectable === true ? firstGroup.parentOption : firstGroup.options[0];
};

const SingleSelectStoreFilter = ({ usedFor, fetchListFor, reduxKeywordToSaveValue }) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();

  const [storeNameOptionSelected, setStoreNameOptionsSelected] = React.useState({});
  const [storesLoaded, setStoresLoaded] = React.useState(false);
  const [retailerAccessLoaded, setRetailerAccessLoaded] = React.useState(false);
  const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);
  const [defaultValuesLoaded, setDefaultValuesLoaded] = React.useState(false);
  const initializedDefaultsRef = React.useRef(false);

  const userRole = storeDetails?.brand_report_view === true ? { user_role: "Brand" } : checkUserRole();
  const behaviorMode = resolveBehaviorMode({ usedFor, fetchListFor });
  const mode = userRole.user_role === "Brand" ? STORE_FILTER_MODES.BRAND : behaviorMode;
  const shouldInitializeRedux = behaviorMode !== STORE_FILTER_MODES.CAMPAIGN;
  const shouldUpdateDateRange = behaviorMode === STORE_FILTER_MODES.PERIOD_BASED;

  const loggedInUserType = resolveLoggedInUserType();
  const workspaceOptionsCount = Array.isArray(storeDetails[WORKSPACE_OPTIONS_KEY])
    ? storeDetails[WORKSPACE_OPTIONS_KEY].length
    : 0;
  const retailerSelectionOptionsCount = Array.isArray(storeDetails[RETAILER_SELECTION_OPTIONS_KEY])
    ? storeDetails[RETAILER_SELECTION_OPTIONS_KEY].length
    : 0;
  const storeNames = React.useMemo(
    () => resolveStoreNames({ mode, behaviorMode, fetchListFor, usedFor, storeDetails }),
    [behaviorMode, fetchListFor, mode, storeDetails, usedFor]
  );
  const storeOptions = React.useMemo(() => toStoreOptions(storeNames), [storeNames]);
  const sortedStoreOptions = React.useMemo(() => sortSelectorOptions(storeOptions), [storeOptions]);

  // Single-select hierarchy uses the same source decision as the multi-select:
  // period filters read root retailer data from store_dates, active filters from stores_list.
  const storeRecordsForOptions = React.useMemo(
    () => resolveStoreRecordsForOptions({ behaviorMode, storeDetails }),
    [behaviorMode, storeDetails]
  );
  const storeOptionsWithRootRetailer = React.useMemo(
    () => toStoreOptionsWithRootRetailer(storeNames, storeRecordsForOptions),
    [storeNames, storeRecordsForOptions]
  );
  const groupedStoreOptions = React.useMemo(
    () =>
      buildGroupedSelectorOptions({
        options: storeOptionsWithRootRetailer,
        groupParam: ROOT_RETAILER_GROUP_PARAM,
      }),
    [storeOptionsWithRootRetailer]
  );

  // Same one-retailer rule as the multi-select store filters.
  const shouldUseRetailerHierarchy = shouldUseRetailerStoreHierarchy({
    isBrand: mode === STORE_FILTER_MODES.BRAND,
    retailerHierarchyAvailable,
    storeOptions: storeOptionsWithRootRetailer,
  });

  const updateDateRange = React.useCallback(
    (selectedStoreNames) => {
      if (!shouldUpdateDateRange) return;

      findMinSelectableDate(selectedStoreNames);
    },
    [shouldUpdateDateRange]
  );

  React.useEffect(() => {
    let shouldApplyResult = true;

    setRetailerAccessLoaded(false);

    const checkRetailerAccess = async () => {
      try {
        const retailerAccessInfo = await loadRetailerAccessAvailability(loggedInUserType);

        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
        }
      } catch {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(false);
        }
      } finally {
        if (shouldApplyResult) {
          setRetailerAccessLoaded(true);
        }
      }
    };

    if (loggedInUserType === "") {
      setRetailerHierarchyAvailable(false);
      setRetailerAccessLoaded(true);
    } else {
      checkRetailerAccess();
    }

    return () => {
      shouldApplyResult = false;
    };
  }, [
    loggedInUserType,
    // BottomNav can hydrate these caches after the filter mounts, so keep the
    // shared availability check responsive to those updates.
    retailerSelectionOptionsCount,
    storesLoaded,
    workspaceOptionsCount,
  ]);

  React.useEffect(() => {
    let isMounted = true;

    initializedDefaultsRef.current = false;
    setStoresLoaded(false);
    setDefaultValuesLoaded(false);
    setBatchLoadingStatus(true);
    // Drop the previous retailer's dates and store selection so the dashboard
    // and dropdown wait for the new defaults instead of showing leftover values.
    const keysToClear = [];
    if (shouldUpdateDateRange === true) {
      keysToClear.push("store_dates");
    }
    if (typeof reduxKeywordToSaveValue !== "undefined") {
      keysToClear.push(reduxKeywordToSaveValue);
    }
    if (keysToClear.length !== 0) {
      dispatchAction(deleteItems(keysToClear));
    }

    const fetchStores = async () => {
      try {
        await loadRequestedStores(behaviorMode, usedFor);
      } finally {
        if (isMounted) {
          setStoresLoaded(true);
        } else {
          setBatchLoadingStatus(false);
        }
      }
    };

    fetchStores();

    return () => {
      isMounted = false;
    };
  }, [behaviorMode, dispatchAction, reduxKeywordToSaveValue, shouldUpdateDateRange, usedFor]);

  React.useEffect(() => {
    // Default selection depends on whether the dropdown renders flat or grouped,
    // so wait until both store loading and hierarchy availability are resolved.
    if (!storesLoaded || !retailerAccessLoaded || initializedDefaultsRef.current) return;

    // After a retailer change we delete store_dates first. Wait only for that
    // gap. An empty array means the API really returned no stores.
    if (shouldUpdateDateRange === true && typeof storeDetails.store_dates === "undefined") {
      return;
    }

    const defaultStoreOption = resolveDefaultStoreOption({
      groupedStoreOptions,
      parentSelectable: STORE_PARENT_SELECTABLE,
      shouldUseRetailerHierarchy,
      sortedStoreOptions,
    });

    initializedDefaultsRef.current = true;

    if (typeof defaultStoreOption === "undefined") {
      setStoreNameOptionsSelected({});
      if (shouldInitializeRedux && typeof reduxKeywordToSaveValue !== "undefined") {
        dispatchAction(
          updateStateData({
            [reduxKeywordToSaveValue]: {
              store_ids: [],
              selected_store_names: [],
            },
          })
        );
      }
      setDefaultValuesLoaded(true);
      setBatchLoadingStatus(false);
      return;
    }

    const defaultStoreName = defaultStoreOption.label;

    setStoreNameOptionsSelected(defaultStoreOption);
    updateDateRange([defaultStoreName]);

    if (
      shouldInitializeRedux &&
      typeof reduxKeywordToSaveValue !== "undefined"
    ) {
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: resolveStoreIds({
              selectedStoreNames: [defaultStoreName],
              behaviorMode,
            }),
            selected_store_names: [defaultStoreOption],
          },
        })
      );
    }

    setDefaultValuesLoaded(true);
    setBatchLoadingStatus(false);
  }, [
    behaviorMode,
    dispatchAction,
    groupedStoreOptions,
    retailerAccessLoaded,
    reduxKeywordToSaveValue,
    shouldUseRetailerHierarchy,
    shouldInitializeRedux,
    shouldUpdateDateRange,
    sortedStoreOptions,
    storeDetails,
    storesLoaded,
    updateDateRange,
  ]);

  if (!defaultValuesLoaded || !retailerAccessLoaded) {
    return <></>;
  }

  // Use the store we just defaulted to, not Redux leftovers from the last retailer
  const selectedStoreOption = storeNameOptionSelected;

  const handleStoreSelectionChange = (selectedOption) => {
    if (
      selectedOption === null ||
      typeof selectedOption === "undefined" ||
      typeof selectedOption.label === "undefined"
    ) {
      setStoreNameOptionsSelected({});
      return;
    }

    const selectedStoreName = selectedOption.label;

    setStoreNameOptionsSelected(selectedOption);
    updateDateRange([selectedStoreName]);

    if (typeof reduxKeywordToSaveValue !== "undefined") {
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: resolveStoreIds({
              selectedStoreNames: [selectedStoreName],
              behaviorMode,
            }),
            selected_store_names: [selectedOption],
          },
        })
      );
    }
  };

  if (shouldUseRetailerHierarchy === true) {
    return (
      <GroupedSingleSelect
        enableFullWidth={false}
        elemId={`${reduxKeywordToSaveValue || usedFor}_store_filter`}
        label={"Store"}
        options={storeOptionsWithRootRetailer}
        defaultVal={selectedStoreOption}
        groupParam={ROOT_RETAILER_GROUP_PARAM}
        selectionCheck={(option, value) => option.label === value.label}
        parentSelectable={STORE_PARENT_SELECTABLE}
        onChangeCall={handleStoreSelectionChange}
      />
    );
  }

  return (
    <SingleSelect
      enableFullWidth={false}
      label={"Store"}
      selectionCheck={(option, value) => option.label === value.label}
      options={storeOptions}
      defaultVal={selectedStoreOption}
      onChangeCall={handleStoreSelectionChange}
    />
  );
};

export default SingleSelectStoreFilter;
